import { getRemoteConfig } from 'firebase/remote-config';

import { MachineServices } from 'common/bento/types/MachineConfig';
import {
  fetchAndActivateRemoteConfig,
  getConfigGettersOnceLoaded,
} from 'common/remoteConfig';
import { isPlaywrightTest } from 'helpers/experiments';

import researchExperimentStorage from '../storage/researchExperiment';
import { Model } from './model';

export enum Service {
  EvaluateResearchExperimentEligibility = 'evaluateResearchExperimentEligibility',
}

const ONE_MINUTE = 60 * 1000;
const CACHE_INVALIDATION_FETCH_INTERVAL = 1;

const EXPERIMENT_ENABLED_KEY = 'isOBJourneyResearchEnabled';
const SHOULD_DISPLAY_RESEARCH_SCREEN_KEY =
  'shouldDisplayOBJourneyResearchPrompt';

const QES_ACCEPTED_LAST_UID_CHARS = new Set<string>([
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
]);

const services: MachineServices<Model, Service> = {
  /**
   * Evaluate whether the current user is eligible to see the research experiment screen.
   *
   * We will use remote config to manage the feature state with two conditions:
   * - One condition will be used to toggle the feature on or off.
   * - One condition will be used to show the experiment to a given percentage of users.
   *
   * By having these two conditions, we can more easily toggle the feature
   * on and off without having to worry about remembering the current %age
   * of users we have defined to see this prompt.
   *
   * Additionally, the remote config uses a cache and is only fetched once per 12 hours,
   * but we want to be able to turn the experiment on or off instantly, which is why we
   * are invalidating the cache here. The volume of users reaching this part of the
   * onboarding is fairly low so this will not bring us above the fetch quota.
   *
   * @returns Whether the current user should see the research experiment
   */
  evaluateResearchExperimentEligibility: async ({ getModuleData }) => {
    const { isMobile, uid } = getModuleData();
    const { getBoolean } = await getConfigGettersOnceLoaded();

    const hasSeenResearchExperiment = researchExperimentStorage.get();

    if (isPlaywrightTest() || isMobile || hasSeenResearchExperiment) {
      return false;
    }

    const config = getRemoteConfig();

    config.settings = {
      fetchTimeoutMillis: ONE_MINUTE,
      minimumFetchIntervalMillis: CACHE_INVALIDATION_FETCH_INTERVAL,
    };

    // Invalidate remote config cache
    await fetchAndActivateRemoteConfig(config);

    const isExperimentEnabled = getBoolean(EXPERIMENT_ENABLED_KEY);
    const shouldDisplayExperiment = getBoolean(
      SHOULD_DISPLAY_RESEARCH_SCREEN_KEY,
    );

    const shouldDoQes = QES_ACCEPTED_LAST_UID_CHARS.has(
      uid[uid.length - 1] as string,
    );

    return isExperimentEnabled && shouldDisplayExperiment && shouldDoQes;
  },
};

export default services;
